import { useState } from "react";
import { Layers, Plus, Store as StoreIcon } from "lucide-react";

import { useRole } from "../hooks/useRole";
import { count, useWords } from "../words";
import { CameraProfileDialog } from "./CameraProfileDialog";
import { SearchableSelect, type SearchableOption } from "./SearchableSelect";
import { useScope } from "./ScopeContext";

const ALL_VALUE = "__all__";
const PROFILE_PREFIX = "profile:";
const STORE_PREFIX = "store:";

/**
 * The store / camera-profile picker that scopes the app.
 *
 * One control for both kinds of scope: an operator thinks "show me the
 * entrance cameras", not "switch selector type, then pick". Switching is
 * instant — every store is already ingesting, so nothing restarts and nothing
 * reloads; the page's queries simply refetch in the new scope.
 *
 * Lives in the sidebar so the viewing scope stays in one place on every page.
 */
export function ScopeSelector({
  className = "",
  collapsed = false,
}: {
  className?: string;
  collapsed?: boolean;
}) {
  const {
    scope,
    stores,
    profiles,
    selectAll,
    selectStore,
    selectProfile,
    loading,
  } = useScope();
  const w = useWords();
  const { canManage, isStaff } = useRole();
  const [creating, setCreating] = useState(false);

  const value =
    scope.kind === "store"
      ? `${STORE_PREFIX}${scope.store}`
      : scope.kind === "profile"
        ? `${PROFILE_PREFIX}${scope.profileId}`
        : ALL_VALUE;

  const live = stores.filter((s) => !s.is_simulation);
  const simulated = stores.filter((s) => s.is_simulation);

  const options: SearchableOption[] = [
    {
      value: ALL_VALUE,
      label: w.allStores,
      hint: stores.length > 0 ? count(stores.length, w.store, w.stores) : `No ${w.stores} yet`,
    },
    ...live.map((s) => ({
      value: `${STORE_PREFIX}${s.name}`,
      label: s.display_name || s.name,
      hint:
        [s.location, `${s.camera_count} camera${s.camera_count === 1 ? "" : "s"}`]
          .filter(Boolean)
          .join(" · ") || undefined,
      keywords: `${s.name} ${s.location}`,
      groupLabel: isStaff ? "Live stores" : w.Stores,
      groupAccent: "from-bf-violet",
    })),
    ...simulated.map((s) => ({
      value: `${STORE_PREFIX}${s.name}`,
      label: s.display_name || s.name,
      hint:
        [s.location, `${s.camera_count} camera${s.camera_count === 1 ? "" : "s"}`]
          .filter(Boolean)
          .join(" · ") || undefined,
      keywords: `${s.name} ${s.location}`,
      groupLabel: "Simulation stores",
      groupAccent: "from-bf-magenta",
    })),
    ...profiles.map((p) => ({
      value: `${PROFILE_PREFIX}${p.id}`,
      label: p.name,
      hint:
        p.members.length > 0
          ? `${p.members.length} camera${p.members.length === 1 ? "" : "s"}` +
            (p.stores.length > 1 ? ` across ${p.stores.length} ${w.stores}` : "")
          : "No cameras yet",
      keywords: `${p.description} ${p.stores.join(" ")}`,
      groupLabel: w.Groups,
      groupAccent: "from-brand-pink",
    })),
  ];

  const onChange = (next: string) => {
    if (next === ALL_VALUE) return selectAll();
    if (next.startsWith(PROFILE_PREFIX)) {
      return selectProfile(next.slice(PROFILE_PREFIX.length));
    }
    return selectStore(next.slice(STORE_PREFIX.length));
  };

  const Icon = scope.kind === "profile" ? Layers : StoreIcon;
  const selectedLabel =
    options.find((o) => o.value === value)?.label ??
    (loading ? "Loading…" : w.allStores);
  return (
    <div className={className}>
      <SearchableSelect
        className="w-full"
        variant="nav"
        compact={collapsed}
        icon={<Icon size={18} className="shrink-0" />}
        triggerLabel={`Select ${w.store}`}
        triggerHint={selectedLabel}
        ariaLabel={`Select ${w.store}`}
        title={`Select ${w.store}`}
        value={value}
        onChange={onChange}
        options={options}
        disabled={loading && stores.length === 0}
        placeholder={loading ? "Loading…" : w.allStores}
        searchPlaceholder={`Search ${w.stores} and ${w.groups}…`}
        emptyMessage={`No ${w.store} or ${w.group} matches.`}
        searchThreshold={5}
        footer={canManage ? (close) => (
          <button
            type="button"
            onClick={() => {
              close();
              setCreating(true);
            }}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] font-medium text-bf-text transition hover:bg-white/5"
          >
            <Plus size={14} className="shrink-0 text-bf-muted" />
            New {w.group}…
          </button>
        ) : undefined}
      />
      {creating && (
        <CameraProfileDialog
          profile={null}
          onClose={() => setCreating(false)}
          onSaved={(saved) => selectProfile(saved.id)}
        />
      )}
    </div>
  );
}
